<template>
  <AdminLayout title="用户管理">
    <div class="user-list-container">
      <!-- 筛选表单 -->
      <div class="filter-form">
        <div class="form-row">
          <div class="form-item">
            <label>用户名</label>
            <div class="input-with-clear">
              <input type="text" v-model="filterForm.username" placeholder="请输入用户名">
              <button v-if="filterForm.username" class="clear-btn" @click="filterForm.username = ''">×</button>
            </div>
          </div>
          <div class="form-item">
            <label>昵称</label>
            <div class="input-with-clear">
              <input type="text" v-model="filterForm.nickname" placeholder="请输入昵称">
              <button v-if="filterForm.nickname" class="clear-btn" @click="filterForm.nickname = ''">×</button>
            </div>
          </div>
          <div class="form-item">
            <label>角色</label>
            <select v-model="filterForm.userRoleCode">
              <option :value="null">全部</option>
              <option :value="1">普通用户</option>
              <option :value="2">管理员</option>
            </select>
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model="filterForm.banned">
              <option :value="null">全部</option>
              <option :value="false">正常</option>
              <option :value="true">已禁用</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-item form-actions">
            <button class="reset-btn" @click="handleReset">重置</button>
            <button class="search-btn" @click="handleSearch">
              <span class="iconify mr-2" data-icon="hugeicons:search-01"></span>搜索
            </button>
            <button class="add-btn" @click="handleAdd">
              <span class="iconify mr-2" data-icon="hugeicons:plus-sign"></span>添加用户
            </button>
          </div>
        </div>
      </div>

      <!-- 用户列表 -->
      <div class="user-table-container">
        <table class="user-table">
          <thead>
            <tr>
              <th>用户名</th>
              <th>昵称</th>
              <th>手机号</th>
              <th>邮箱</th>
              <th>角色</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in userList" :key="user.uuid">
              <td>{{ user.username }}</td>
              <td>{{ user.nickname }}</td>
              <td>{{ user.phone || '-' }}</td>
              <td>{{ user.email || '-' }}</td>
              <td>
                <span class="role-tag" :class="user.userRoleCode === 2 ? 'role-admin' : 'role-normal'">
                  {{ user.userRoleCode === 2 ? '管理员' : '普通用户' }}
                </span>
              </td>
              <td>
                <span class="status-tag" :class="user.banned ? 'status-banned' : 'status-normal'">
                  {{ user.banned ? '已禁用' : '正常' }}
                </span>
              </td>
              <td>{{ formatDate(user.createdAt) }}</td>
              <td>
                <button v-if="canOperateUser(user)" class="action-btn edit-btn" @click="handleEdit(user)">编辑</button>
                <button v-if="canOperateUser(user)" class="action-btn reset-btn" @click="handleResetPassword(user)">重置密码</button>
                <button v-if="canOperateUser(user)" class="action-btn delete-btn" @click="handleDelete(user)">删除</button>
                <span v-if="!canOperateUser(user)" class="no-permission-hint">-</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="userList.length === 0" class="empty-state">
          <span class="iconify" data-icon="hugeicons:empty-state-04"></span>
          <p>暂无用户数据</p>
        </div>
      </div>

      <!-- 分页 -->
      <div class="pagination" v-if="pagination.total > 0">
        <div class="pagination-info">共 {{ pagination.total }} 条记录，第 {{ pagination.current }} / {{ pagination.pages }} 页</div>
        <div class="pagination-controls">
          <button class="page-btn" @click="handlePageChange(1)" :disabled="pagination.current === 1">首页</button>
          <button class="page-btn" @click="handlePageChange(pagination.current - 1)" :disabled="pagination.current === 1">上一页</button>
          <button v-for="page in pageArray" :key="page" class="page-btn" :class="{ active: page === pagination.current }" @click="handlePageChange(page)">{{ page }}</button>
          <button class="page-btn" @click="handlePageChange(pagination.current + 1)" :disabled="pagination.current === pagination.pages">下一页</button>
          <button class="page-btn" @click="handlePageChange(pagination.pages)" :disabled="pagination.current === pagination.pages">末页</button>
        </div>
      </div>
    </div>

    <!-- 添加/编辑用户模态框 -->
    <div v-if="showUserModal" class="modal-overlay" @click="closeUserModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ isEdit ? '编辑用户' : '添加用户' }}</h3>
          <button class="close-btn" @click="closeUserModal"><span class="iconify" data-icon="hugeicons:cross-01"></span></button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>用户名 <span class="required">*</span></label>
            <div class="input-with-copy">
              <input type="text" v-model="userForm.username" :disabled="isEdit" placeholder="请输入用户名">
              <button v-if="!isEdit && userForm.username" type="button" class="copy-btn" @click="copyUsername" title="复制用户名">
                <span class="iconify" data-icon="hugeicons:copy-01"></span>
              </button>
            </div>
          </div>
          <div class="form-item" v-if="!isEdit">
            <label>密码 <span class="required">*</span></label>
            <div class="password-input-row">
              <input :type="showPassword ? 'text' : 'password'" v-model="userForm.password" placeholder="请输入密码">
              <button type="button" class="toggle-password-btn" @click="showPassword = !showPassword">
                <span class="iconify" :data-icon="showPassword ? 'hugeicons:view-off' : 'hugeicons:view'"></span>
              </button>
              <button type="button" class="generate-btn" @click="generatePassword">随机生成</button>
              <button v-if="userForm.password" type="button" class="copy-btn" @click="copyPassword" title="复制密码">
                <span class="iconify" data-icon="hugeicons:copy-01"></span>
              </button>
            </div>
            <div class="password-hint" :class="{ 'hint-error': passwordError }">{{ passwordHint }}</div>
          </div>
          <div class="form-item" v-if="!isEdit && userForm.username && userForm.password">
            <button type="button" class="copy-all-btn" @click="copyUsernameAndPassword">
              <span class="iconify" data-icon="hugeicons:copy-01"></span>
              一键复制用户名和密码
            </button>
          </div>
          <div class="form-item">
            <label>昵称 <span class="required">*</span></label>
            <input type="text" v-model="userForm.nickname" placeholder="请输入昵称">
          </div>
          <div class="form-item">
            <label>手机号</label>
            <input type="text" v-model="userForm.phone" placeholder="请输入手机号">
          </div>
          <div class="form-item">
            <label>邮箱</label>
            <input type="email" v-model="userForm.email" placeholder="请输入邮箱">
          </div>
          <div class="form-item" v-if="isEdit">
            <label>状态</label>
            <select v-model="userForm.banned">
              <option :value="false">正常</option>
              <option :value="true">禁用</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeUserModal">取消</button>
          <button class="confirm-btn" @click="submitUser" :disabled="loading">{{ loading ? '提交中...' : '确认' }}</button>
        </div>
      </div>
    </div>

    <!-- 重置密码模态框 -->
    <div v-if="showResetModal" class="modal-overlay" @click="closeResetModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>重置密码</h3>
          <button class="close-btn" @click="closeResetModal"><span class="iconify" data-icon="hugeicons:cross-01"></span></button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label>用户</label>
            <div class="input-with-copy">
              <input type="text" :value="resetTargetUser?.username" disabled>
              <button type="button" class="copy-btn" @click="copyResetUsername" title="复制用户名">
                <span class="iconify" data-icon="hugeicons:copy-01"></span>
              </button>
            </div>
          </div>
          <div class="form-item">
            <label>新密码 <span class="required">*</span></label>
            <div class="password-input-row">
              <input :type="showResetPassword ? 'text' : 'password'" v-model="resetPasswordForm.password" placeholder="请输入新密码">
              <button type="button" class="toggle-password-btn" @click="showResetPassword = !showResetPassword">
                <span class="iconify" :data-icon="showResetPassword ? 'hugeicons:view-off' : 'hugeicons:view'"></span>
              </button>
              <button type="button" class="generate-btn" @click="generateResetPassword">随机生成</button>
              <button v-if="resetPasswordForm.password" type="button" class="copy-btn" @click="copyResetPassword" title="复制密码">
                <span class="iconify" data-icon="hugeicons:copy-01"></span>
              </button>
            </div>
            <div class="password-hint" :class="{ 'hint-error': resetPasswordError }">{{ resetPasswordHint }}</div>
          </div>
          <div class="form-item" v-if="resetPasswordForm.password">
            <button type="button" class="copy-all-btn" @click="copyResetUsernameAndPassword">
              <span class="iconify" data-icon="hugeicons:copy-01"></span>
              一键复制用户名和密码
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeResetModal">取消</button>
          <button class="confirm-btn" @click="submitResetPassword" :disabled="loading">{{ loading ? '提交中...' : '确认' }}</button>
        </div>
      </div>
    </div>

    <!-- 消息提示 -->
    <div v-if="message" class="message-toast" :class="messageType">{{ message }}</div>
  </AdminLayout>
</template>

<script>
import AdminLayout from '../../components/manage/ManageLayout.vue';
import { request } from '../../utils/request';
import JSEncrypt from 'jsencrypt';
import config from '../../config';

const API_BASE_URL = config.API_BASE_URL;

export default {
  name: 'UserList',
  components: { AdminLayout },
  data() {
    return {
      filterForm: {
        username: '',
        nickname: '',
        userRoleCode: null,
        banned: null,
        page: 1,
        size: 10
      },
      userList: [],
      pagination: { total: 0, current: 1, pages: 0, size: 10 },
      loading: false,
      publicKey: '',
      // 用户模态框
      showUserModal: false,
      isEdit: false,
      userForm: { uuid: '', username: '', password: '', nickname: '', phone: '', email: '', banned: false },
      showPassword: false,
      passwordError: false,
      // 重置密码模态框
      showResetModal: false,
      resetTargetUser: null,
      resetPasswordForm: { password: '' },
      showResetPassword: false,
      resetPasswordError: false,
      // 消息
      message: '',
      messageType: '',
      // 当前用户角色
      currentUserRoleCode: 0
    };
  },
  computed: {
    pageArray() {
      const { current, pages } = this.pagination;
      const pageNum = [];
      if (pages <= 5) {
        for (let i = 1; i <= pages; i++) pageNum.push(i);
      } else {
        let start = Math.max(1, current - 2);
        let end = Math.min(pages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        for (let i = start; i <= end; i++) pageNum.push(i);
      }
      return pageNum;
    },
    passwordHint() {
      return this.validatePassword(this.userForm.password);
    },
    resetPasswordHint() {
      return this.validatePassword(this.resetPasswordForm.password);
    }
  },
  mounted() {
    this.getCurrentUserRole();
    this.getUserList();
  },
  methods: {
    async getCurrentUserRole() {
      try {
        const response = await request(`${API_BASE_URL}/api/user/profile`, {
          body: JSON.stringify({})
        });
        if (response.code === '200') {
          this.currentUserRoleCode = response.data.userRoleCode || 0;
        }
      } catch (error) {
        console.error('获取当前用户信息失败', error);
      }
    },
    canOperateUser(user) {
      // 只有当前用户角色等级大于目标用户时才能操作
      return this.currentUserRoleCode > user.userRoleCode;
    },
    async getPublicKey() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/open/auth/public-key`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        if (data.code === '200') {
          this.publicKey = data.data.publicKey;
        }
      } catch (error) {
        this.showMessage('获取公钥失败', 'error');
      }
    },
    encryptData(data) {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(this.publicKey);
      return encrypt.encrypt(data);
    },
    validatePassword(password) {
      if (!password) return '请输入密码';
      if (password.length < 8) return '密码长度至少为8位';
      let typeCount = 0;
      if (/[0-9]/.test(password)) typeCount++;
      if (/[a-z]/.test(password)) typeCount++;
      if (/[A-Z]/.test(password)) typeCount++;
      if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)) typeCount++;
      if (typeCount < 3) return '密码必须包含数字、大写字母、小写字母、特殊符号中的至少三种';
      return '密码强度符合要求';
    },
    isPasswordWeak(password) {
      return this.validatePassword(password) !== '密码强度符合要求';
    },
    generatePassword() {
      const chars = {
        digit: '0123456789',
        lower: 'abcdefghijklmnopqrstuvwxyz',
        upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
        special: '!@#$%^&*'
      };
      let password = '';
      // 确保每种类型至少一个
      password += chars.digit[Math.floor(Math.random() * chars.digit.length)];
      password += chars.lower[Math.floor(Math.random() * chars.lower.length)];
      password += chars.upper[Math.floor(Math.random() * chars.upper.length)];
      password += chars.special[Math.floor(Math.random() * chars.special.length)];
      // 填充到12位
      const allChars = chars.digit + chars.lower + chars.upper + chars.special;
      for (let i = 4; i < 12; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }
      // 打乱顺序
      this.userForm.password = password.split('').sort(() => Math.random() - 0.5).join('');
    },
    generateResetPassword() {
      const chars = {
        digit: '0123456789',
        lower: 'abcdefghijklmnopqrstuvwxyz',
        upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
        special: '!@#$%^&*'
      };
      let password = '';
      password += chars.digit[Math.floor(Math.random() * chars.digit.length)];
      password += chars.lower[Math.floor(Math.random() * chars.lower.length)];
      password += chars.upper[Math.floor(Math.random() * chars.upper.length)];
      password += chars.special[Math.floor(Math.random() * chars.special.length)];
      const allChars = chars.digit + chars.lower + chars.upper + chars.special;
      for (let i = 4; i < 12; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }
      this.resetPasswordForm.password = password.split('').sort(() => Math.random() - 0.5).join('');
    },
    async getUserList() {
      this.loading = true;
      try {
        const response = await request(`${API_BASE_URL}/api/user/list`, {
          body: JSON.stringify(this.filterForm)
        });
        if (response.code === '200') {
          this.userList = response.data.records;
          this.pagination = response.data;
        }
      } catch (error) {
        this.showMessage('获取用户列表失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.filterForm.page = 1;
      this.getUserList();
    },
    handleReset() {
      this.filterForm = { username: '', nickname: '', userRoleCode: null, banned: null, page: 1, size: 10 };
      this.getUserList();
    },
    handlePageChange(page) {
      if (page >= 1 && page <= this.pagination.pages) {
        this.filterForm.page = page;
        this.getUserList();
      }
    },
    handleAdd() {
      this.isEdit = false;
      this.userForm = { uuid: '', username: '', password: '', nickname: '', phone: '', email: '', banned: false };
      this.showPassword = false;
      this.passwordError = false;
      this.showUserModal = true;
    },
    handleEdit(user) {
      this.isEdit = true;
      this.userForm = { ...user };
      this.showUserModal = true;
    },
    closeUserModal() {
      this.showUserModal = false;
    },
    async submitUser() {
      // 验证
      if (!this.userForm.username) {
        this.showMessage('请输入用户名', 'error');
        return;
      }
      if (!this.isEdit) {
        if (!this.userForm.password) {
          this.showMessage('请输入密码', 'error');
          return;
        }
        if (this.isPasswordWeak(this.userForm.password)) {
          this.passwordError = true;
          this.showMessage(this.passwordHint, 'error');
          return;
        }
      }
      if (!this.userForm.nickname) {
        this.showMessage('请输入昵称', 'error');
        return;
      }

      this.loading = true;
      try {
        await this.getPublicKey();
        let url, body;
        if (this.isEdit) {
          url = `${API_BASE_URL}/api/user/update`;
          body = JSON.stringify(this.userForm);
        } else {
          url = `${API_BASE_URL}/api/user/create`;
          body = JSON.stringify({
            encryptedUsername: this.encryptData(this.userForm.username),
            encryptedPassword: this.encryptData(this.userForm.password),
            nickname: this.userForm.nickname,
            phone: this.userForm.phone,
            email: this.userForm.email
          });
        }
        const response = await request(url, { body });
        if (response.code === '200') {
          this.showMessage(this.isEdit ? '修改成功' : '创建成功', 'success');
          this.closeUserModal();
          this.getUserList();
        } else {
          this.showMessage(response.message || '操作失败', 'error');
        }
      } catch (error) {
        this.showMessage('操作失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    handleResetPassword(user) {
      this.resetTargetUser = user;
      this.resetPasswordForm.password = '';
      this.showResetPassword = false;
      this.resetPasswordError = false;
      this.showResetModal = true;
    },
    closeResetModal() {
      this.showResetModal = false;
      this.resetTargetUser = null;
    },
    async submitResetPassword() {
      if (!this.resetPasswordForm.password) {
        this.showMessage('请输入新密码', 'error');
        return;
      }
      if (this.isPasswordWeak(this.resetPasswordForm.password)) {
        this.resetPasswordError = true;
        this.showMessage(this.resetPasswordHint, 'error');
        return;
      }

      this.loading = true;
      try {
        await this.getPublicKey();
        const response = await request(`${API_BASE_URL}/api/user/reset-password`, {
          body: JSON.stringify({
            uuid: this.resetTargetUser.uuid,
            encryptedPassword: this.encryptData(this.resetPasswordForm.password)
          })
        });
        if (response.code === '200') {
          this.showMessage('密码重置成功', 'success');
          this.closeResetModal();
        } else {
          this.showMessage(response.message || '重置失败', 'error');
        }
      } catch (error) {
        this.showMessage('重置失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    handleDelete(user) {
      if (confirm(`确定要删除用户 "${user.username}" 吗？`)) {
        this.deleteUser(user.uuid);
      }
    },
    async deleteUser(uuid) {
      try {
        const response = await request(`${API_BASE_URL}/api/user/delete`, {
          body: JSON.stringify({ uuid })
        });
        if (response.code === '200') {
          this.showMessage('删除成功', 'success');
          this.getUserList();
        } else {
          this.showMessage(response.message || '删除失败', 'error');
        }
      } catch (error) {
        this.showMessage('删除失败', 'error');
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    copyUsername() {
      navigator.clipboard.writeText(this.userForm.username).then(() => {
        this.showMessage('用户名已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    copyPassword() {
      navigator.clipboard.writeText(this.userForm.password).then(() => {
        this.showMessage('密码已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    copyUsernameAndPassword() {
      const text = `用户名：${this.userForm.username}\n密码：${this.userForm.password}`;
      navigator.clipboard.writeText(text).then(() => {
        this.showMessage('用户名和密码已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    copyResetUsername() {
      navigator.clipboard.writeText(this.resetTargetUser?.username || '').then(() => {
        this.showMessage('用户名已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    copyResetPassword() {
      navigator.clipboard.writeText(this.resetPasswordForm.password).then(() => {
        this.showMessage('密码已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    copyResetUsernameAndPassword() {
      const text = `用户名：${this.resetTargetUser?.username}\n密码：${this.resetPasswordForm.password}`;
      navigator.clipboard.writeText(text).then(() => {
        this.showMessage('用户名和密码已复制', 'success');
      }).catch(() => {
        this.showMessage('复制失败', 'error');
      });
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;
      setTimeout(() => { this.message = ''; }, 3000);
    }
  }
};
</script>

<style scoped>
.user-list-container { background-color: white; border-radius: 8px; padding: 24px; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1); }
.filter-form { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e5e7eb; }
.form-row { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.form-item { display: flex; flex-direction: column; gap: 8px; min-width: 200px; flex: 1; }
.form-item label { font-size: 14px; font-weight: 500; color: #374151; }
.form-item input, .form-item select { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; width: 100%; }
.form-item input:focus, .form-item select:focus { outline: none; border-color: #3b82f6; }
.form-actions { align-self: flex-end; flex-direction: row; gap: 12px; }
.search-btn, .add-btn { padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px; }
.search-btn { background-color: #3b82f6; color: white; }
.search-btn:hover { background-color: #2563eb; }
.add-btn { background-color: #10b981; color: white; }
.add-btn:hover { background-color: #059669; }
.reset-btn { padding: 10px 24px; background-color: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; }
.reset-btn:hover { background-color: #e5e7eb; }
.input-with-clear { position: relative; display: flex; align-items: center; }
.clear-btn { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6b7280; font-size: 20px; font-weight: bold; background: transparent; border: none; }
.input-with-clear input { padding-right: 32px; }
.input-with-copy { position: relative; display: flex; align-items: center; }
.input-with-copy input { padding-right: 36px; }
.copy-btn { padding: 6px 8px; background: none; border: 1px solid #d1d5db; border-radius: 6px; cursor: pointer; color: #6b7280; display: flex; align-items: center; justify-content: center; }
.copy-btn:hover { background-color: #f3f4f6; color: #3b82f6; }
.copy-all-btn { width: 100%; padding: 10px 16px; background-color: #3b82f6; color: white; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
.copy-all-btn:hover { background-color: #2563eb; }
.user-table-container { margin-bottom: 24px; overflow-x: auto; }
.user-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.user-table th, .user-table td { padding: 12px 16px; text-align: left; border-bottom: 1px solid #e5e7eb; }
.user-table th { background-color: #f9fafb; font-weight: 600; color: #374151; }
.user-table tr:hover { background-color: #f9fafb; }
.role-tag { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
.role-admin { background-color: #fee2e2; color: #991b1b; }
.role-normal { background-color: #dbeafe; color: #1e40af; }
.status-tag { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
.status-normal { background-color: #d1fae5; color: #059669; }
.status-banned { background-color: #fee2e2; color: #991b1b; }
.action-btn { padding: 6px 12px; border: none; border-radius: 4px; font-size: 12px; cursor: pointer; margin-right: 8px; }
.edit-btn { background-color: #3b82f6; color: white; }
.reset-btn { background-color: #f59e0b; color: white; }
.delete-btn { background-color: #ef4444; color: white; }
.no-permission-hint { color: #9ca3af; }
.empty-state { text-align: center; padding: 64px 24px; color: #9ca3af; }
.empty-state .iconify { font-size: 64px; margin-bottom: 16px; opacity: 0.5; }
.pagination { display: flex; justify-content: space-between; align-items: center; padding-top: 24px; border-top: 1px solid #e5e7eb; flex-wrap: wrap; gap: 16px; }
.pagination-info { font-size: 14px; color: #6b7280; }
.pagination-controls { display: flex; gap: 8px; }
.page-btn { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; background-color: white; font-size: 14px; cursor: pointer; }
.page-btn:hover:not(:disabled) { background-color: #f3f4f6; }
.page-btn.active { background-color: #3b82f6; color: white; border-color: #3b82f6; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background-color: white; border-radius: 8px; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #e5e7eb; }
.modal-header h3 { margin: 0; font-size: 18px; font-weight: 600; color: #374151; }
.close-btn { background: none; border: none; font-size: 20px; cursor: pointer; color: #6b7280; padding: 4px; }
.modal-body { padding: 24px; }
.modal-footer { display: flex; justify-content: flex-end; gap: 12px; padding: 16px 24px; border-top: 1px solid #e5e7eb; }
.required { color: #ef4444; }
.password-input-row { display: flex; gap: 8px; align-items: center; }
.password-input-row input { flex: 1; }
.toggle-password-btn { padding: 8px; background: none; border: 1px solid #d1d5db; border-radius: 6px; cursor: pointer; color: #6b7280; }
.generate-btn { padding: 8px 12px; background-color: #f3f4f6; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; cursor: pointer; white-space: nowrap; }
.password-hint { font-size: 12px; color: #6b7280; margin-top: 4px; }
.hint-error { color: #ef4444; }
.cancel-btn { padding: 10px 24px; background-color: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; cursor: pointer; }
.confirm-btn { padding: 10px 24px; background-color: #3b82f6; color: white; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; }
.confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.message-toast { position: fixed; top: 20px; left: 50%; transform: translateX(-50%); padding: 12px 24px; border-radius: 6px; font-size: 14px; z-index: 2000; }
.message-toast.success { background-color: #d1fae5; color: #059669; }
.message-toast.error { background-color: #fee2e2; color: #991b1b; }
</style>
